import { Remoteable, Supertype, supertypeClass } from '@haventech/amorphic';
import { POLICY_TYPES } from '../../common/js/constants/policy';
import { Category } from '../../common/js/logger/CPLogger';
import { Applicant } from '../../common/js/customer/Applicant';
import { Utils } from '../../common/js/Utils';
import { CoverpathPolicy } from '../classes/coverpath_subclasses/CoverpathPolicy';
import { CoverpathTermQuote } from '../classes/quote/coverpath/CoverpathTermQuote';
import { ProductType } from '../../common/js/interfaces/CommonTypes';
import { Product, RiderType } from '../../common/js/static/Product';
import { Exchange1035 } from '../../common/js/quote/coverpath/Exchange1035';
import { OwnerCategory } from '../js/interfaces/CoverpathTypes';
import { Trustee } from '../../common/js/customer/Trustee';
import { CasApplication } from '../classes/coverpath_subclasses/CasApplication';
import { CoverpathUtils } from '../js/static/CoverpathUtils';
import { CoverpathWholeLifeQuote } from '../classes/quote/coverpath/CoverpathWholeLifeQuote';
import { QuoteExchange1035 } from '../../common/js/quote/coverpath/QuoteExchange1035';
import { CommonUtils } from '../../common/js/static/CommonUtils';
import { PolicyRider } from '../../common/js/customer/PolicyRider';
import type { BaseQuote } from '../../common/js/quote/BaseQuote';
import type { CoverpathCustomer } from '../classes/coverpath_subclasses/CoverpathCustomer';
import type { ExistingPolicyType } from '../../common/js/customer/ExistingPolicy';
import type { QuoteRider } from '../../common/js/quote/QuoteRider';
import type {
  ClientInformation,
  QuoteForm,
  WholeLifeQuote,
} from '../../coverpath-agent/public/js/components/home-new/quote-or-apply-new/QuoteForm';
import { LoggerSingleton } from '../../common/js/logger/LoggerSingleton';

@supertypeClass({ toClient: false, toServer: true })
export class ObjectGraphController extends Remoteable(Supertype) {
  /**
   * Build a Coverpath Customer from the reactive forms object.
   *
   * @param {QuoteForm} quoteForm - Reactive form quote JSON object
   *
   * @return {CoverpathCustomer} Customer amorphic object
   */
  static async buildCustomerObjectGraph(
    customer: CoverpathCustomer,
    quoteForm: QuoteForm,
    isTeleCMIEnabled: boolean,
    lowFaceWholeLifeEnabled?: boolean
  ): Promise<CoverpathCustomer> {
    const { clientInformation, productType, termQuote, wholeLifeQuote, jointAppQuote } = quoteForm;
    clientInformation.dob = this.adjustDob(clientInformation.dob);
    const {
      ownerFirstName,
      ownerLastName,
      insuredFirstName,
      insuredLastName,
      ownerEmail,
      contractState,
      ownerEqualsInsured,
      gender,
      dob,
      citizenship,
    } = clientInformation;
    const insured: Applicant = Applicant.new(customer);
    insured.person.firstName = ownerEqualsInsured ? ownerFirstName : insuredFirstName;
    insured.person.lastName = ownerEqualsInsured ? ownerLastName : insuredLastName;
    insured.person.email = ownerEqualsInsured ? ownerEmail : undefined;
    insured.address.state = contractState;
    insured.person.age = Utils.getActualAge(dob); // this needs to change?
    insured.person.gender = gender;
    insured.person.dob = new Date(dob);
    insured.person.familyMembersTrigger();
    insured.person.residencyStatus = citizenship;

    let quote: BaseQuote,
      product: string,
      policyProductType: ProductType,
      secondaryQuote: BaseQuote,
      secondaryProduct: string,
      secondaryPolicyProductType: ProductType,
      wholeLifePolicy: WholeLifeQuote;

    switch (productType) {
      case ProductType.TERM:
        quote = this.prepareTermQuote({ ...termQuote, ...clientInformation });
        quote.customer = customer;
        product = termQuote.ecp ? Product.products.CASTermECP.code : Product.products.CASTerm.code;
        policyProductType = ProductType.TERM;
        break;
      case ProductType.WHOLE:
        quote = this.prepareWholeLifeQuote({ ...wholeLifeQuote, ...clientInformation });
        quote.customer = customer;
        product = wholeLifeQuote.product;
        policyProductType = ProductType.WHOLE;
        wholeLifePolicy = wholeLifeQuote;
        break;
      case ProductType.JOINTAPP:
        quote = this.prepareWholeLifeQuote({ ...jointAppQuote.wholeLifeQuote, ...clientInformation });
        quote.customer = customer;
        secondaryQuote = this.prepareTermQuote({ ...jointAppQuote.termQuote, ...clientInformation });
        quote.secondaryQuote = secondaryQuote;
        secondaryQuote.customer = customer;
        product = jointAppQuote.wholeLifeQuote.product;
        policyProductType = ProductType.WHOLE;
        secondaryProduct = jointAppQuote.termQuote.ecp
          ? Product.products.CASTermECP.code
          : Product.products.CASTerm.code;
        secondaryPolicyProductType = ProductType.TERM;
        wholeLifePolicy = jointAppQuote.wholeLifeQuote;
        break;
      default:
        const err = {
          isHumanRelated: false,
          data: {
            productType: productType,
          },
        };
        LoggerSingleton.getInstance().error(
          {
            module: 'ObjectGraphController',
            function: 'buildCustomerObjectGraph',
            category: Category.security,
            error: err,
          },
          'Unrecognized product type'
        );
        throw new Error('Unrecognized product type: ' + productType);
    }
    // add quote
    customer.insuranceQuotes.push(quote);

    const application = await this.createApplication(customer);
    application.isAgentAssistant = false;
    customer.application = application;
    quote.application = application;

    // create policy
    const policy = CoverpathPolicy.new(customer);
    policy.insured = insured;
    policy.updateWithQuoteInfo(quote);
    policy.type = POLICY_TYPES.PRIMARY;
    policy.product = product;
    policy.productType = policyProductType;
    this.assignPolicyOwner(policy, clientInformation);

    if (!Utils.isProdEnv()) {
      if (ownerLastName.match(/.+\s\+live-\S+/)) {
        policy.setRunModes(ownerLastName.substring(ownerLastName.indexOf('+live-')));
      }
    }
    application.quote = quote;
    policy.application = application;
    application.addPolicy(policy);

    //TODO with ticket HL-49932: Remove Instances of isTeleCMIEnabled feature flag
    if (isTeleCMIEnabled) {
      application.isTeleCMI = policy.isTeleCMIRequired();
    }
    policy.linkSentDate = Utils.now();

    quote.riders.forEach((r) => {
      const rider: PolicyRider = PolicyRider.new(r.type, policy);
      rider.amount = r.amount;
      rider.premiumBase = r.premiumBase;
      rider.premiumWaiver = r.premiumWaiver;
      rider.plannedYears = r.plannedYears;
      rider.paymentFrequency = r.paymentFrequency ? r.paymentFrequency.toLowerCase() : null; // toLowerCase() to be in sync with paymentFrequency capitalization on policy object
      rider.hasScheduledPayments = r.hasScheduledPayments;
      rider.benefitDisbursementMethod = r.benefitDisbursementMethod;
      rider.predictedBenefitAge = r.predictedBenefitAge;
      rider.puaAddedToBenefitPool = r.puaAddedToBenefitPool;
      rider.monthlyIncreaseOption = r.monthlyIncreaseOption;
      policy.riders.push(rider);
    });

    if (policy.hasCirRider) {
      policy.hasCirRiderTrigger();
    }

    if (Utils.exists(wholeLifePolicy) && wholeLifePolicy.exchange1035.enabled) {
      insured.existingPolicies.hasExistingInforcePolicies = true;
      const { cashValueTransfer, policyType } = wholeLifePolicy.exchange1035;
      insured.existingPolicies.addCashValueReplacement(cashValueTransfer, policyType as ExistingPolicyType);
      policy.hasAlirRider = true;
      policy.hasAlirScheduledPayments = false;
      policy.alirLumpSumUses1035 = true;
      const quoteExchange1035 = wholeLifePolicy.exchange1035;
      policy.alirRider.exchange1035 = Exchange1035.new(
        quoteExchange1035.cashValueTransfer,
        quoteExchange1035.costBasis,
        quoteExchange1035.policyType,
        quoteExchange1035.applyTowards,
        quoteExchange1035.expectedArrival,
        quoteExchange1035.isMec
      );
      policy.hasAlirLumpSum = false;
    }

    if (!!secondaryQuote) {
      const secondaryPolicy = CoverpathPolicy.new(customer);
      secondaryPolicy.updateWithQuoteInfo(secondaryQuote);
      secondaryPolicy.type = POLICY_TYPES.SECONDARY;
      secondaryPolicy.product = secondaryProduct;
      secondaryPolicy.productType = secondaryPolicyProductType;
      secondaryPolicy.owner = policy.owner;
      application.addPolicy(secondaryPolicy);
    }
    LoggerSingleton.getInstance().info(
      {
        module: 'ObjectGraphController',
        function: 'buildCustomerObjectGraph',
        category: Category.security,
        data: {
          customer: customer._id,
          application: application._id,
          productType: productType,
        },
      },
      'Unrecognized product type'
    );
    return customer;
  }

  static adjustDob(dob: Date) {
    dob = new Date(dob);
    dob.setUTCHours(12);
    return dob;
  }

  static assignPolicyOwner(policy: CoverpathPolicy, clientInformation: ClientInformation): void {
    const {
      ownerCategory,
      ownerEqualsInsured,
      ownerFirstName,
      ownerLastName,
      ownerEmail,
      contractState,
      ownerEntityName,
      ownerEntityType,
      ownerTrusteeType,
      ownerTrusteeEntityName,
      relationshipToTrust,
    } = clientInformation;
    if (ownerCategory === OwnerCategory.BUSINESS || ownerCategory === OwnerCategory.TRUST) {
      policy.ownerClassification = 'trust';
      policy.ownerClassificationTrigger();
      policy.insuredType = 'generalbusiness';
      policy.ownerTypeSelection = 'trust';
      policy.ownerTypeSelectionTrigger();
      policy.trustOrOtherEntity.legalAddress.state = contractState;
      policy.trustOrOtherEntity.trustSitus = contractState;
      policy.trustOrOtherEntity.authorizedSignerFirstName = ownerFirstName;
      policy.trustOrOtherEntity.authorizedSignerLastName = ownerLastName;
      policy.trustOrOtherEntity.authorizedSignerEmail = ownerEmail;
      policy.trustOrOtherEntity.authorizedSigner.firstName = policy.trustOrOtherEntity.authorizedSignerFirstName;
      policy.trustOrOtherEntity.authorizedSigner.lastName = policy.trustOrOtherEntity.authorizedSignerLastName;
      policy.trustOrOtherEntity.authorizedSigner.email = policy.trustOrOtherEntity.authorizedSignerEmail;
      policy.trustOrOtherEntity.email = policy.trustOrOtherEntity.authorizedSignerEmail;
      if (ownerCategory === OwnerCategory.TRUST) {
        policy.trustOrOtherEntity.type = 'trust';
        policy.trustOrOtherEntity.trustName = ownerEntityName;
        const trustee: Trustee = Trustee.new(null);
        trustee.type = ownerTrusteeType;
        if (trustee.type === 'Individual') {
          trustee.trusteePerson = policy.trustOrOtherEntity.authorizedSigner;
        } else {
          trustee.entityName = ownerTrusteeEntityName;
          trustee.entityAuthorizedSigner = policy.trustOrOtherEntity.authorizedSigner;
        }
        policy.trustOrOtherEntity.authorizedTrusteeList[0] = trustee;
        policy.trustOrOtherEntity.designatedTrusteeIndex = 0;
        policy.trustOrOtherEntity.designatedTrusteeIndexTrigger();
        policy.insuredType = relationshipToTrust;
      } else {
        policy.trustOrOtherEntity.type = ownerEntityType;
        policy.trustOrOtherEntity.name = ownerEntityName;
      }
    } else {
      policy.ownerPersonResidentialAddress.state = contractState;
      policy.ownerPerson.firstName = ownerFirstName;
      policy.ownerPerson.lastName = ownerLastName;
      policy.ownerPerson.email = ownerEmail;
      if (ownerEqualsInsured) {
        policy.insuredType = 'you';
        policy.ownerType = 'you';
      } else if (policy.isNewYorkApplication()) {
        policy.insuredType = 'notYou';
      }
    }
  }

  private static async createApplication(customer: CoverpathCustomer) {
    const application = CasApplication.new(customer);
    await application.assignApplicationNumber();
    customer.application = application;
    return application;
  }

  private static prepareTermQuote({
    contractState,
    gender,
    dob,
    healthClass,
    faceAmount,
    waiver,
    waiverOfPremium,
    premium,
    coverageTerm,
    ecp,
    paymentFrequency,
    effectiveDate,
    citizenship,
    showPremiumToClient,
  }) {
    const quote = new CoverpathTermQuote();
    quote.hasEcp = ecp;
    quote.gender = gender;
    quote.citizenship = citizenship;
    quote.dateOfBirth = new Date(dob);
    quote.premium = premium;
    quote.smoker = false;
    quote.healthClassNumber = CoverpathUtils.getRateClassDescriptions('healthClassNumber')[healthClass];
    quote.contractState = contractState;
    quote.term = coverageTerm;
    quote.faceAmount = faceAmount;
    quote.healthClass = healthClass;
    quote.waiverOfPremium = waiverOfPremium;
    quote.waiverOfPremiumAmount = waiver;
    quote.isAgentAssistant = false;
    quote.carrierCode = 'CAS';
    quote.paymentFrequency = paymentFrequency;
    quote.effectiveDate = effectiveDate;
    quote.showPremiumToClient = showPremiumToClient;
    return quote;
  }

  private static prepareWholeLifeQuote({
    contractState,
    gender,
    dob,
    healthClass,
    faceAmount,
    waiver,
    waiverOfPremium,
    premium,
    dividendOption,
    dividendInterestAdjustment,
    loanType,
    taxBracket,
    paymentFrequency,
    product,
    riders,
    exchange1035,
    bankSale,
    effectiveDate,
    citizenship,
    showPremiumToClient,
  }) {
    const faceAndRtrAmount = riders?.rtr?.enabled ? faceAmount + riders.rtr.amount : faceAmount;
    const healthClassToUse = CommonUtils.determineRatingClass(
      healthClass,
      faceAndRtrAmount,
      product,
      ProductType.WHOLE
    ).stringCode;
    const quote = new CoverpathWholeLifeQuote();
    quote.dividend = dividendOption;
    quote.dividendInterestAdjustment = dividendInterestAdjustment ? dividendInterestAdjustment : 0;
    quote.showPremiumToClient = showPremiumToClient;
    quote.loanType = loanType;
    quote.taxBracket = taxBracket;
    quote.gender = gender;
    quote.citizenship = citizenship;
    quote.dateOfBirth = dob;
    quote.premium = premium;
    quote.smoker = false;
    quote.contractState = contractState;
    quote.term = Product.productSpecs[product].term;
    quote.faceAmount = faceAmount;
    quote.healthClass = healthClassToUse;
    quote.waiverOfPremium = waiverOfPremium;
    quote.waiverOfPremiumAmount = waiver;
    quote.isAgentAssistant = false;
    quote.carrierCode = 'CAS';
    quote.paymentFrequency = paymentFrequency;
    quote.product = product;
    if (riders.gir.enabled) {
      const girRider: QuoteRider = quote.addRider(RiderType.GIR);
      girRider.amount = riders.gir.amount;
      girRider.premiumBase = riders.gir.premium;
      girRider.premiumWaiver = riders.gir.waiver;
    }
    if (riders.rtr.enabled) {
      const rtrRider: QuoteRider = quote.addRider(RiderType.RTR);
      rtrRider.amount = riders.rtr.amount;
      rtrRider.premiumBase = riders.rtr.premium;
      rtrRider.premiumWaiver = riders.rtr.waiver;
    }
    if (riders.alir.enabled) {
      const alirRider: QuoteRider = quote.addRider(RiderType.ALIR);
      alirRider.amount = riders.alir.amount;
      alirRider.plannedYears = riders.alir.plannedYears;
      alirRider.paymentFrequency = riders.alir.paymentFrequency;
      // premium = amount for ALIR
      alirRider.premiumBase = riders.alir.premium;
      // waiver = 0 for ALIR
      alirRider.premiumWaiver = riders.alir.waiver;
      alirRider.hasScheduledPayments = riders.alir.enabled;
    }
    if (riders.cir.enabled) {
      const cirRider: QuoteRider = quote.addRider(RiderType.CIR);
      cirRider.amount = riders.cir.amount;
      cirRider.premiumBase = riders.cir.premium;
      cirRider.premiumWaiver = riders.cir.waiver;
      cirRider.benefitDisbursementMethod = riders.cir.benefitDisbursementMethod;
      cirRider.predictedBenefitAge = riders.cir.predictedBenefitAge;
      cirRider.puaAddedToBenefitPool = riders.cir.puaAddedToBenefitPool;
      cirRider.monthlyIncreaseOption = riders.cir.monthlyIncreaseOption;
    }
    if (exchange1035.enabled) {
      quote.quoteExchange1035 = new QuoteExchange1035(
        exchange1035.cashValueTransfer,
        exchange1035.costBasis,
        exchange1035.policyType,
        exchange1035.applyTowards,
        exchange1035.expectedArrival,
        exchange1035.isMec,
        quote
      );
    }
    quote.bankSale = bankSale;
    quote.effectiveDate = effectiveDate;
    return quote;
  }
}
